import { Button } from "@components/ui/Button"
import { IconPicker } from "@components/ui/IconPicker"
import { Input } from "@components/ui/Input"
import { Modal } from "@components/ui/Modal"
import { Select } from "@components/ui/Select"
import { useToast } from "@components/ui/Toaster"
import { logger } from "@lib/logger"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useArchiveClassroom, useUpdateClassroom } from "../hooks"

import type { UpdateClassroomPayload } from "@/api/classrooms"
import { defaultIconForSubject } from "@/constants/classroomIcons"
import { SEMESTER_CHOICES, type Semester } from "@/constants/semester"
import { apiErrorCode } from "@/lib/apiError"
import type { Classroom } from "@/types"

/**
 * EditClassroomModal — single focused surface to edit a Salón's name (alias),
 * icon and school year. It replaces the old inline card editing; one PATCH carries the
 * changed fields. (PRD-47 v2 — the education level is campus-wide and
 * admin-owned, so it is no longer editable here.)
 *
 * Name semantics mirror the old inline rename: an empty name OR one equal to the
 * canonical label resets `classroom_alias` to null (fall back to the computed
 * name). Icon semantics mirror the picker: choosing the subject default stores
 * "" so the icon keeps following the subject.
 */
interface Props {
  open: boolean
  onClose: () => void
  classroom: Classroom
}

// PRD-45 — the modal has two views sharing one `<Modal>` shell rather
// than stacking a second dialog on top: "edit" (the existing form,
// with the archive entry point in its own bordered-off footer
// section) and "archive-confirm" (a single, reassurance-first confirm —
// NOT the double-click destructive pattern used elsewhere, since
// archiving is reversible and deletes nothing).
type ModalView = "edit" | "archive-confirm"

export function EditClassroomModal({ open, onClose, classroom }: Props) {
  const { t } = useTranslation(["classrooms", "errors", "common"])
  const toast = useToast()
  const update = useUpdateClassroom()
  const archive = useArchiveClassroom()
  const [name, setName] = useState(classroom.effective_display_name)
  const [icon, setIcon] = useState(classroom.icon || classroom.effective_icon)
  // PRD-61 — the year decides which evaluation calendar the classroom
  // reads; a classroom created under last year's setting kept it.
  const [semester, setSemester] = useState<string>(classroom.semester)
  const [view, setView] = useState<ModalView>("edit")

  // Re-seed the fields each time the modal opens (or the classroom changes)
  // so a previous unsaved edit never leaks into the next open.
  useEffect(() => {
    if (open) {
      setName(classroom.effective_display_name)
      setIcon(classroom.icon || classroom.effective_icon)
      setSemester(classroom.semester)
      setView("edit")
    }
  }, [open, classroom])

  const handleArchiveConfirm = () => {
    archive.mutate(classroom.id, {
      onSuccess: () => {
        toast.success(t("archive.success"))
        onClose()
      },
      onError: (err) => {
        const code = apiErrorCode(err)
        toast.error(t(`errors:${code}`, t("archive.error")))
        logger.error("classroom.archive.failed", { classroomId: classroom.id, code })
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const data: UpdateClassroomPayload = {}

    // Name → alias (null resets to canonical_label).
    const trimmed = name.trim()
    const desiredAlias = !trimmed || trimmed === classroom.canonical_label ? null : trimmed
    if (desiredAlias !== (classroom.classroom_alias ?? null)) {
      data.classroom_alias = desiredAlias
    }

    // Icon → "" when it equals the subject default (keep following the subject),
    // else the explicit name.
    const desiredIcon =
      icon === defaultIconForSubject(classroom.subject_default_code ?? "") ? "" : icon
    if (desiredIcon !== classroom.icon) {
      data.icon = desiredIcon
    }
    if (semester !== classroom.semester) {
      data.semester = semester
    }
    if (Object.keys(data).length === 0) {
      onClose()
      return
    }

    update.mutate(
      { id: classroom.id, data },
      {
        onSuccess: () => {
          toast.success(t("modal.save_success"))
          onClose()
        },
        onError: (err) => {
          const code = apiErrorCode(err)
          toast.error(t(`errors:${code}`, t("modal.save_error")))
          logger.error("classroom.edit.failed", { classroomId: classroom.id, code })
        },
      },
    )
  }

  if (view === "archive-confirm") {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t("archive.confirm_title", { name: classroom.effective_display_name })}
        closeLabel={t("common:actions.close")}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setView("edit")}
              disabled={archive.isPending}
            >
              {t("archive.confirm_cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleArchiveConfirm}
              loading={archive.isPending}
            >
              {t("archive.confirm_cta")}
            </Button>
          </div>
        }
      >
        <div className="text-ink space-y-3 text-sm">
          <p>{t("archive.confirm_body", { count: classroom.total_students })}</p>
          <p className="text-meta">{t("archive.confirm_reassurance")}</p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("modal.title")}
      closeLabel={t("common:actions.close")}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("modal.cancel")}
          </Button>
          <Button type="submit" form="edit-classroom-form" loading={update.isPending}>
            {t("modal.save")}
          </Button>
        </div>
      }
    >
      <form id="edit-classroom-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1 text-sm">
          <span className="text-ink font-medium">{t("form.icon")}</span>
          <div>
            <IconPicker
              value={icon}
              subject={classroom.subject_default_code ?? ""}
              onChange={setIcon}
              onReset={() => setIcon(defaultIconForSubject(classroom.subject_default_code ?? ""))}
              label={t("icon.edit_aria")}
              searchPlaceholder={t("icon.search_placeholder")}
              resetLabel={t("icon.reset")}
            />
          </div>
        </div>

        {/* The `Input` primitive, not a hand-rolled field: it owns the focus
            ring, the hint slot and the error wiring this one re-spelled. The
            visible label plus an `aria-label` saying the same words also made
            a screen reader read the field's name twice (PRD-59). */}
        <div className="space-y-1 text-sm">
          <label htmlFor="edit-classroom-name" className="text-ink font-medium">
            {t("modal.name_label")}
          </label>
          <Input
            id="edit-classroom-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint={t("modal.name_hint", { label: classroom.canonical_label })}
          />
        </div>

        <div className="space-y-1 text-sm">
          <label id="edit-classroom-semester-label" className="text-ink font-medium">
            {t("modal.semester_label")}
          </label>
          <Select<Semester>
            value={semester as Semester}
            options={SEMESTER_CHOICES.map((year) => ({ value: year, label: year }))}
            onChange={setSemester}
            ariaLabelledBy="edit-classroom-semester-label"
            className="w-full"
          />
          <p className="text-meta">{t("modal.semester_hint")}</p>
        </div>

        {/* PRD-47 v2 — the education-level selector is gone: the level is
            the campus's, set by the admin platform, and cascades here. */}
      </form>

      {/* PRD-45 §FR-3.2 — a bordered-off footer section, not another
          always-visible card-level button: archiving is low-frequency
          and deserves a deliberate second step, not a primary action
          competing with "Ver alumnos"/"Ver materiales". */}
      <div className="border-hairline mt-6 border-t pt-4">
        <p className="text-ink text-sm font-medium">{t("archive.section_title")}</p>
        <p className="text-meta mt-1">{t("archive.section_body")}</p>
        <Button
          type="button"
          variant="ghost"
          className="text-danger-ink hover:bg-danger-soft mt-2"
          onClick={() => setView("archive-confirm")}
        >
          {t("archive.cta")}
        </Button>
      </div>
    </Modal>
  )
}
